import React, { useState, useEffect } from 'react';
import './ActualitesPage.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import actualiteService from '../services/actualiteService';
import { getImageUrl, handleImageError } from '../utils/imageUtils';
import { toSentenceCase } from '../utils/textUtils';
import useSEO from '../hooks/useSEO';

// SVG Icons (Strictly no emojis/stickers)
const IconYouTube = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

const IconPlay = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <polygon points="5 3 19 12 5 21 5 3"/>
  </svg>
);

const IconExternalLink = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
    <polyline points="15 3 21 3 21 9"/>
    <line x1="10" y1="14" x2="21" y2="3"/>
  </svg>
);

const IconClose = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const IconCheckCircle = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
    <polyline points="22 4 12 14.01 9 11.01"/>
  </svg>
);

const IconSearch = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/>
    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

const IconCalendar = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);

const IconNewspaper = ({ size = 48 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2Zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2"/>
    <path d="M18 14h-8"/>
    <path d="M15 18h-5"/>
    <path d="M10 6h8v4h-8z"/>
  </svg>
);

const IconArrowLeft = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12"/>
    <polyline points="12 19 5 12 12 5"/>
  </svg>
);

const IconArrowRight = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/>
    <polyline points="12 5 19 12 12 19"/>
  </svg>
);

const YOUTUBE_CHANNEL_URL = "https://youtube.com/@cpulespalmiers4431?si=klEjrjQXcjvkGADV";
const YOUTUBE_CHANNEL_HANDLE = "@cpulespalmiers4431";

// VRAIES VIDÉOS OFFICIELLES (LISTE INITIALE DE SECOURS + SYNCHRO DYNAMIQUE)
const initialYoutubeVideos = [
  {
    id: "qVuOR_ydkuo",
    title: "Les stages seconde 2nde MM-V au CPET Les Palmiers de Togba",
    category: "Formation & Stages",
    badge: "Reportage Vedette",
    duration: "Vidéo Officielle",
    thumbnail: "https://img.youtube.com/vi/qVuOR_ydkuo/hqdefault.jpg",
    description: "Immersion au cœur des ateliers et suivi des stages professionnels des élèves de la classe de 2nde Métiers de la Mode et Vêtements (MM-V) au CPET Les Palmiers de Togba.",
    youtubeUrl: "https://www.youtube.com/watch?v=qVuOR_ydkuo",
    embedUrl: "https://www.youtube.com/embed/qVuOR_ydkuo?autoplay=1&rel=0&enablejsapi=1",
    highlightTag: "Filière MM-V"
  },
  {
    id: "5idjfq8VF8M",
    title: "Orientation après le BEPC en formation professionnelle",
    category: "Orientation & Métiers",
    badge: "Guide Pratique",
    duration: "Conseils d'experts",
    thumbnail: "https://img.youtube.com/vi/5idjfq8VF8M/hqdefault.jpg",
    description: "Guide complet d'orientation post-BEPC : opportunités d'avenir, cycles de formation technique et débouchés professionnels offerts par le GSU & CPET Les Palmiers.",
    youtubeUrl: "https://www.youtube.com/watch?v=5idjfq8VF8M",
    embedUrl: "https://www.youtube.com/embed/5idjfq8VF8M?autoplay=1&rel=0&enablejsapi=1",
    highlightTag: "Post-BEPC"
  },
  {
    id: "QSY-K2X0fRM",
    title: "Les filières disponibles dans les collèges privés techniques 2026-2027",
    category: "Filières & Formations",
    badge: "Rentrée 2026-2027",
    duration: "Guide Filières",
    thumbnail: "https://img.youtube.com/vi/QSY-K2X0fRM/hqdefault.jpg",
    description: "Présentation des filières techniques disponibles : Métiers de la Mode (MM-V), Électrotechnique (F3), Installation & Maintenance Informatique, etc.",
    youtubeUrl: "https://www.youtube.com/watch?v=QSY-K2X0fRM",
    embedUrl: "https://www.youtube.com/embed/QSY-K2X0fRM?autoplay=1&rel=0&enablejsapi=1",
    highlightTag: "Formations"
  },
  {
    id: "IVykrZ8XDps",
    title: "Une machine offerte à une élève passante en 1ère MM-V 2026-2027",
    category: "Excellence & Récompenses",
    badge: "Remise de Prix",
    duration: "Cérémonie",
    thumbnail: "https://img.youtube.com/vi/IVykrZ8XDps/hqdefault.jpg",
    description: "Encouragement à l'excellence : remise solennelle d'équipements de couture professionnels à une élève brillante passant en 1ère MM-V.",
    youtubeUrl: "https://www.youtube.com/watch?v=IVykrZ8XDps",
    embedUrl: "https://www.youtube.com/embed/IVykrZ8XDps?autoplay=1&rel=0&enablejsapi=1",
    highlightTag: "Bourses & Dons"
  }
];

function ActualitesPage() {
  useSEO({
    title: "Actualités & Vie Scolaire | Événements, Réussites & Chaîne YouTube",
    description: "Suivez toute l'actualité du Groupe Scolaire et Universitaire Les Palmiers : événements scolaires, réussites académiques, reportages vidéo YouTube et vie à Togba.",
    keywords: "actualités GSU Les Palmiers, chaîne YouTube Les Palmiers Togba, événements scolaires Calavi, résultats BAC G2 Bénin, actualités éducation Bénin",
    canonicalUrl: "https://gsulespalmiers.bj/actualites"
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('Toutes');
  const [actualites, setActualites] = useState([]);
  const [youtubeVideos, setYoutubeVideos] = useState(initialYoutubeVideos);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);

  const categories = [
    'Toutes',
    'Vidéos & Reportages',
    'Événements',
    'Filières & Métiers',
    'Palmarès & Bourses'
  ];

  useEffect(() => {
    loadActualites();
    syncLatestYoutubeChannelVideos();
  }, []);

  // Récupération dynamique et automatique des vidéos nouvellement publiées sur la chaîne YouTube
  const syncLatestYoutubeChannelVideos = async () => {
    try {
      // Flux RSS public converti en JSON via rss2json
      const feedUrl = encodeURIComponent('https://www.youtube.com/feeds/videos.xml?user=cpulespalmiers4431');
      const res = await fetch(`https://api.rss2json.com/v1/api.json?rss_url=${feedUrl}`);
      const data = await res.json();

      if (data && data.status === 'ok' && Array.isArray(data.items) && data.items.length > 0) {
        const fetchedVideos = data.items.map((item, idx) => {
          const videoId = item.guid?.replace('yt:video:', '') || extractYouTubeId(item.link) || 'qVuOR_ydkuo';
          return {
            id: videoId,
            title: item.title,
            category: 'Formation & Reportages',
            badge: idx === 0 ? 'Dernière Publication YouTube' : 'Nouveau Reportage',
            duration: 'Vidéo Officielle',
            thumbnail: item.thumbnail || `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
            description: item.description ? item.description.replace(/<[^>]*>?/gm, '').substring(0, 160) + '...' : 'Vidéo officielle publiée sur la chaîne YouTube Les Palmiers.',
            youtubeUrl: item.link || `https://www.youtube.com/watch?v=${videoId}`,
            embedUrl: `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&enablejsapi=1`,
            highlightTag: 'Direct YouTube'
          };
        });

        // Combiner avec la liste tout en évitant les doublons
        setYoutubeVideos(prev => {
          const merged = [...fetchedVideos];
          prev.forEach(p => {
            if (!merged.some(m => m.id === p.id)) {
              merged.push(p);
            }
          });
          return merged;
        });
      }
    } catch (err) {
      console.log('Synchro YouTube automatique en arrière-plan:', err);
    }
  };

  const loadActualites = async () => {
    try {
      setLoading(true);
      const response = await actualiteService.getAll();
      const data = response.data;
      if (Array.isArray(data)) {
        setActualites(data);
      } else if (data && Array.isArray(data.data)) {
        setActualites(data.data);
      } else {
        setActualites([]);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des actualités:', error);
      setActualites([]);
    } finally {
      setLoading(false);
    }
  };

  // Extraire l'ID YouTube si présent dans l'image ou le contenu
  const extractYouTubeId = (urlOrText) => {
    if (!urlOrText) return null;
    const match = urlOrText.match(/\/vi\/([a-zA-Z0-9_-]{11})\//) || urlOrText.match(/(?:watch\?v=|embed\/|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    return match ? match[1] : null;
  };

  // Filtrer les actualités selon la catégorie
  const filteredByCategory = activeCategory === 'Toutes' && Array.isArray(actualites)
    ? actualites 
    : Array.isArray(actualites) 
      ? actualites.filter(act => {
          if (activeCategory === 'Vidéos & Reportages') {
            return act.categorie === 'Vidéos & Reportages' || act.titre?.toLowerCase().includes('vidéo') || act.titre?.toLowerCase().includes('reportage');
          }
          return act.categorie === activeCategory;
        })
      : [];

  // Filtrer par recherche
  const filteredActualites = searchTerm
    ? filteredByCategory.filter(act => 
        act.titre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        act.contenu?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        act.description?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : filteredByCategory;

  const handleViewDetails = (slug) => {
    if (window.navigateTo) {
      window.navigateTo('actualite-detail', slug);
    }
  };

  const handleOpenVideo = (video) => {
    setSelectedVideo(video);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setSelectedVideo(null);
      }
    };
    if (selectedVideo) {
      window.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [selectedVideo]);

  const featuredVideo = youtubeVideos[0] || initialYoutubeVideos[0];
  const sideVideos = youtubeVideos.slice(1, 4);

  return (
    <div className="actualites-page">
      <Header activePage="actualites" />
      
      {/* Hero Section Actualités */}
      <section className="actualites-hero">
        <div className="hero-background">
          <div className="hero-overlay"></div>
        </div>
        <div className="container">
          <div className="hero-content">
            <div className="actualites-hero-badge">
              <span className="live-dot"></span>
              <span>Actualités, Médias & Chaîne Vidéo Officielle</span>
            </div>
            <h1 className="hero-title">Actualités & Reportages Vidéos</h1>
            <p className="hero-description">
              Découvrez toute la vie du Groupe Scolaire et Universitaire Les Palmiers : reportages en immersion, démonstrations techniques en ateliers, cérémonies de réussite et grands événements.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION VEDETTE : CHAÎNE YOUTUBE OFFICIELLE AVEC SYNCHRO EN TEMPS RÉEL */}
      <section className="youtube-spotlight-section">
        <div className="container">
          <div className="youtube-channel-header-card">
            <div className="yt-header-left">
              <div className="yt-avatar-box">
                <IconYouTube size={24} />
              </div>
              <div className="yt-channel-info">
                <div className="yt-badge-official">
                  <IconCheckCircle size={15} />
                  <span>Chaîne YouTube Officielle</span>
                </div>
                <h2 className="yt-channel-title">GSU & CPET Les Palmiers</h2>
                <p className="yt-channel-handle">{YOUTUBE_CHANNEL_HANDLE} • Reportages, TP Ateliers & Événements en direct</p>
              </div>
            </div>

            <div className="yt-header-right">
              <a 
                href={YOUTUBE_CHANNEL_URL} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="btn-yt-subscribe"
                title="S'abonner à la chaîne YouTube officielle"
              >
                <IconYouTube size={18} />
                <span>S'abonner à la chaîne</span>
                <IconExternalLink size={14} />
              </a>
            </div>
          </div>

          {/* Grille Vidéos YouTube en Vedette */}
          <div className="youtube-videos-grid">
            {/* Grande vidéo principale (Featured) */}
            {featuredVideo && (
              <div className="yt-featured-card" onClick={() => handleOpenVideo(featuredVideo)}>
                <div className="yt-video-thumb-wrap">
                  <img 
                    src={featuredVideo.thumbnail} 
                    alt={featuredVideo.title}
                    className="yt-video-thumb"
                  />
                  <div className="yt-thumb-overlay"></div>
                  <div className="yt-play-btn-large">
                    <IconPlay size={28} />
                  </div>
                  <span className="yt-duration-badge">{featuredVideo.duration}</span>
                  <span className="yt-badge-tag">{featuredVideo.badge}</span>
                </div>
                <div className="yt-card-body">
                  <div className="yt-card-meta">
                    <span className="yt-category-pill">{featuredVideo.category}</span>
                  </div>
                  <h3 className="yt-card-title">{featuredVideo.title}</h3>
                  <p className="yt-card-desc">{featuredVideo.description}</p>
                  <div className="yt-card-actions">
                    <button className="btn-watch-video">
                      <IconPlay size={15} />
                      <span>Regarder la vidéo</span>
                    </button>
                    <a 
                      href={featuredVideo.youtubeUrl} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="btn-yt-external"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span>Sur YouTube</span>
                      <IconExternalLink size={14} />
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* Cartes vidéos secondaires */}
            <div className="yt-secondary-list">
              {sideVideos.map((video) => (
                <div key={video.id} className="yt-side-video-card" onClick={() => handleOpenVideo(video)}>
                  <div className="yt-side-thumb-wrap">
                    <img 
                      src={video.thumbnail} 
                      alt={video.title}
                      className="yt-side-thumb"
                    />
                    <div className="yt-side-play-btn">
                      <IconPlay size={18} />
                    </div>
                    <span className="yt-side-duration">{video.duration}</span>
                  </div>
                  <div className="yt-side-body">
                    <span className="yt-side-tag">{video.category}</span>
                    <h4 className="yt-side-title">{video.title}</h4>
                    <div className="yt-side-footer">
                      <span className="yt-side-watch-link">Regarder</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Barre de Recherche et Onglets Catégories */}
      <section className="categories-nav">
        <div className="container">
          <div className="search-box">
            <input
              type="text"
              placeholder="Rechercher un article, un reportage vidéo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button className="search-btn" aria-label="Rechercher">
              <IconSearch size={18} />
            </button>
          </div>
          
          <div className="categories-wrapper">
            <div className="categories-list">
              {categories.map((category, index) => (
                <button
                  key={index}
                  className={`category-tab ${activeCategory === category ? 'active' : ''}`}
                  onClick={() => setActiveCategory(category)}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Grille des Articles et Actualités */}
      <section className="actualites-list section">
        <div className="container">
          <div className="section-intro-header">
            <h2 className="section-title-modern">
              {activeCategory === 'Toutes' ? 'Tous les Articles & Reportages' : `Actualités : ${activeCategory}`}
            </h2>
            <p className="section-subtitle-modern">
              Retrouvez l'ensemble des parutions officielles, comptes rendus pédagogiques et actualités de la vie scolaire.
            </p>
          </div>

          {loading ? (
            <div className="actualites-loading-box">
              <div className="spinner-loader"></div>
              <p>Chargement des actualités en cours...</p>
            </div>
          ) : filteredActualites.length === 0 ? (
            <div className="actualites-empty-box">
              <div className="empty-icon-wrapper" style={{ margin: '0 auto 1rem', width: 60, height: 60, borderRadius: '50%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
                <IconNewspaper size={32} />
              </div>
              <h3>Aucune actualité trouvée</h3>
              <p>Aucun article ne correspond à votre recherche ou à cette catégorie pour le moment.</p>
              <button 
                className="btn-reset-filters"
                onClick={() => { setSearchTerm(''); setActiveCategory('Toutes'); }}
              >
                Réinitialiser les filtres
              </button>
            </div>
          ) : (
            <div className="actualites-grid">
              {filteredActualites.map((actualite) => {
                const ytId = extractYouTubeId(actualite.image) || extractYouTubeId(actualite.contenu);
                const isVideo = Boolean(ytId) || actualite.categorie === 'Vidéos & Reportages' || 
                                actualite.titre?.toLowerCase().includes('vidéo') || 
                                actualite.titre?.toLowerCase().includes('reportage');
                const finalYtId = ytId || 'qVuOR_ydkuo';

                return (
                  <article key={actualite.id} className={`actualite-card-modern ${isVideo ? 'card-video-type' : ''}`}>
                    <div className="actualite-image-box">
                      <img 
                        src={ytId ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg` : getImageUrl(actualite.image, '/images/logo.svg')} 
                        alt={actualite.titre}
                        onError={(e) => handleImageError(e, '/images/logo.svg')}
                      />

                      {/* Badge Vidéo ou Catégorie */}
                      {isVideo ? (
                        <span className="badge-video-glow">
                          <IconPlay size={13} />
                          <span>Vidéo Reportage</span>
                        </span>
                      ) : (
                        <span className="actualite-badge">{actualite.categorie || 'Actualité'}</span>
                      )}

                      {isVideo && (
                        <div 
                          className="overlay-play-action"
                          onClick={() => handleOpenVideo({
                            id: finalYtId,
                            title: actualite.titre,
                            description: actualite.description || actualite.contenu,
                            category: actualite.categorie,
                            date: new Date(actualite.date_publication).toLocaleDateString('fr-FR'),
                            thumbnail: `https://img.youtube.com/vi/${finalYtId}/hqdefault.jpg`,
                            youtubeUrl: `https://www.youtube.com/watch?v=${finalYtId}`,
                            embedUrl: `https://www.youtube.com/embed/${finalYtId}?autoplay=1&rel=0&enablejsapi=1`
                          })}
                        >
                          <div className="play-pulse-circle">
                            <IconPlay size={22} />
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="actualite-body">
                      <div className="actualite-meta-row">
                        <span className="actualite-date-text">
                          <IconCalendar size={14} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 4 }} />
                          {new Date(actualite.date_publication).toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })}
                        </span>
                        {isVideo && <span className="meta-yt-source">Chaîne YouTube</span>}
                      </div>

                      <h3 className="actualite-title">{toSentenceCase(actualite.titre)}</h3>
                      
                      <p className="actualite-snippet">
                        {toSentenceCase(actualite.description || actualite.contenu?.substring(0, 140) + '...')}
                      </p>

                      <div className="actualite-card-footer">
                        {isVideo ? (
                          <div className="video-card-actions-row">
                            <button 
                              className="btn-card-play-now"
                              onClick={() => handleOpenVideo({
                                id: finalYtId,
                                title: actualite.titre,
                                description: actualite.description || actualite.contenu,
                                category: actualite.categorie,
                                date: new Date(actualite.date_publication).toLocaleDateString('fr-FR'),
                                thumbnail: `https://img.youtube.com/vi/${finalYtId}/hqdefault.jpg`,
                                youtubeUrl: `https://www.youtube.com/watch?v=${finalYtId}`,
                                embedUrl: `https://www.youtube.com/embed/${finalYtId}?autoplay=1&rel=0&enablejsapi=1`
                              })}
                            >
                              <IconPlay size={14} />
                              <span>Lancer la vidéo</span>
                            </button>
                            <button 
                              className="btn-read-more"
                              onClick={() => handleViewDetails(actualite.slug)}
                            >
                              <span>Détails</span>
                              <IconArrowRight size={14} />
                            </button>
                          </div>
                        ) : (
                          <button 
                            className="btn-read-more"
                            onClick={() => handleViewDetails(actualite.slug)}
                          >
                            <span>Lire l'article complet</span>
                            <IconArrowRight size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* MODALE LECTEUR VIDÉO INTERACTIF HD */}
      {selectedVideo && (
        <div className="video-modal-overlay" onClick={() => setSelectedVideo(null)}>
          {/* Bouton flottant retour rapide */}
          <button 
            className="btn-modal-floating-back" 
            onClick={() => setSelectedVideo(null)}
            aria-label="Retour aux actualités"
          >
            <IconArrowLeft size={16} />
            <span>Retour</span>
          </button>

          <div className="video-modal-container" onClick={(e) => e.stopPropagation()}>
            {/* BARRE SUPÉRIEURE DE NAVIGATION */}
            <div className="video-modal-header-bar">
              <button 
                className="btn-modal-back-top" 
                onClick={() => setSelectedVideo(null)}
                aria-label="Retour aux actualités"
              >
                <IconArrowLeft size={16} />
                <span>Retour aux actualités</span>
              </button>

              <div className="modal-header-center-info">
                <span className="modal-header-live-tag">
                  <IconPlay size={12} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 4 }} />
                  En lecture
                </span>
              </div>

              <button 
                className="btn-close-modal" 
                onClick={() => setSelectedVideo(null)}
                aria-label="Fermer la vidéo"
                title="Fermer (Échap)"
              >
                <IconClose size={18} />
              </button>
            </div>

            {/* Lecteur Vidéo YouTube Embed Autoplay */}
            <div className="video-modal-player-wrap">
              <iframe
                src={selectedVideo.embedUrl || `https://www.youtube.com/embed/${selectedVideo.id}?autoplay=1&rel=0&enablejsapi=1`}
                title={selectedVideo.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="modal-youtube-player-iframe"
              ></iframe>
            </div>

            <div className="video-modal-details">
              <div className="modal-tags-bar">
                <span className="modal-category-tag">{selectedVideo.category || 'Reportage Vidéo'}</span>
                <span className="modal-channel-tag">
                  <IconYouTube size={14} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 4 }} />
                  YouTube @cpulespalmiers4431
                </span>
              </div>

              <h2 className="modal-video-title">{selectedVideo.title}</h2>
              <p className="modal-video-desc">{selectedVideo.description}</p>

              <div className="modal-footer-actions">
                <button 
                  className="btn-modal-close-prominent"
                  onClick={() => setSelectedVideo(null)}
                >
                  <IconArrowLeft size={16} />
                  <span>Retour à la liste des actualités</span>
                </button>

                <a 
                  href={selectedVideo.youtubeUrl || `https://www.youtube.com/watch?v=${selectedVideo.id}`} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="btn-modal-watch-yt"
                >
                  <IconYouTube size={16} />
                  <span>Ouvrir sur YouTube</span>
                  <IconExternalLink size={14} />
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

export default ActualitesPage;
